export type CountRow = {
  label: string;
  count: number;
};

export type PriceBand = {
  label: string;
  count: number;
};

export type SampleListing = {
  source: "Explore55Plus" | "Where55";
  name: string;
  location: string;
  state: string;
  price: string;
  hoa: string;
  url: string;
  signals: string[];
  note: string;
};

export type ActiveAdultSummary = {
  generatedAt: string;
  scopeNote: string;
  totals: {
    explore55PlusRichRows: number;
    explore55PlusInventoryUrls: number;
    where55RichRows: number;
    where55InventoryUrls: number;
    pricedExploreRows: number;
  };
  medians: {
    priceMin: number;
    priceMax: number;
  };
  topStates: CountRow[];
  topSignals: CountRow[];
  priceBands: PriceBand[];
  sampleListings: SampleListing[];
};

export type PainCategory = {
  id: string;
  label: string;
  threadCount: number;
  commentCount: number;
  plainLanguage: string;
  likelyPaidHelp: string[];
};

export type PaySignal = {
  id: string;
  label: string;
  postCount: number;
  commentCount: number;
};

export type PainSummary = {
  generatedAt: string;
  corpus: {
    threads: number;
    sampledComments: number;
    subreddits: string[];
    sourceCount: number;
    failedSourceCount: number;
  };
  categories: PainCategory[];
  paySignals: PaySignal[];
  bestInitialOfferHypothesis: string;
};

export type LocationRow = {
  id: string;
  location: string;
  region: string;
  archetype: string;
  bestFit: string[];
  avoidIf: string[];
  reviewSignal: string;
  riskFlags: string[];
  evidenceLinks: string[];
  evidenceConfidence: "low" | "medium" | "high";
};

export type ResourceRow = {
  id: string;
  title: string;
  kind: "data" | "analysis" | "source-map" | "model" | "site";
  path: string;
  focus: string;
  rowCount?: number;
};

export type DashboardMetric = {
  label: string;
  value: string;
  detail: string;
};

export const activeAdultSummary: ActiveAdultSummary = {
  generatedAt: "2026-05-16T05:13:13.059Z",
  scopeNote:
    "Explore55Plus has rich signed-in community-card metadata; Where55 contributes a larger sitemap inventory with limited rich-card extraction.",
  totals: {
    explore55PlusRichRows: 670,
    explore55PlusInventoryUrls: 1320,
    where55RichRows: 8,
    where55InventoryUrls: 2412,
    pricedExploreRows: 667,
  },
  medians: {
    priceMin: 300000,
    priceMax: 800000,
  },
  topStates: [
    { label: "Florida", count: 221 },
    { label: "California", count: 69 },
    { label: "Arizona", count: 66 },
    { label: "New Jersey", count: 44 },
    { label: "North Carolina", count: 42 },
    { label: "South Carolina", count: 33 },
    { label: "Texas", count: 25 },
    { label: "Virginia", count: 21 },
    { label: "Nevada", count: 20 },
    { label: "Georgia", count: 15 },
  ],
  topSignals: [
    { label: "large scale", count: 384 },
    { label: "new build", count: 381 },
    { label: "golf", count: 304 },
    { label: "clubs", count: 243 },
    { label: "gated", count: 176 },
    { label: "water", count: 166 },
    { label: "pickleball", count: 155 },
    { label: "trails", count: 103 },
    { label: "master planned", count: 64 },
    { label: "town center", count: 46 },
  ],
  priceBands: [
    { label: "Under $200K", count: 55 },
    { label: "$200K-$399K", count: 372 },
    { label: "$400K-$599K", count: 193 },
    { label: "$600K-$799K", count: 34 },
    { label: "$800K+", count: 13 },
  ],
  sampleListings: [
    {
      source: "Explore55Plus",
      name: "Lakewood Ranch",
      location: "Sarasota / Bradenton, Florida",
      state: "Florida",
      price: "$300K-$1M",
      hoa: "$100-$650/mo",
      url: "https://www.explore55plus.com/community/lakewood-ranch-florida",
      signals: ["clubs", "trails", "healthcare", "master_planned", "town_center", "large_scale"],
      note: "Master-planned scale with trails, hospitals, villages, and walkable town centers.",
    },
    {
      source: "Explore55Plus",
      name: "The Villages",
      location: "The Villages / Ocala, Florida",
      state: "Florida",
      price: "$250K-$1M",
      hoa: "$164-$189/mo",
      url: "https://www.explore55plus.com/community/the-villages-florida",
      signals: ["golf", "clubs", "new_build", "town_center", "large_scale"],
      note: "The archetypal high-scale active-adult social and recreation operating system.",
    },
    {
      source: "Explore55Plus",
      name: "Green Valley Recreation",
      location: "Tucson / Green Valley, Arizona",
      state: "Arizona",
      price: "$100K-$550K",
      hoa: "$45-$400/mo",
      url: "https://www.explore55plus.com/community/green-valley-recreation-arizona",
      signals: ["pickleball", "clubs", "new_build"],
      note: "Lower entry price with a recreation network across many subdivisions.",
    },
    {
      source: "Explore55Plus",
      name: "Sun City",
      location: "Phoenix - West Valley, Arizona",
      state: "Arizona",
      price: "$100K-$700K",
      hoa: "$0/mo",
      url: "https://www.explore55plus.com/community/sun-city-arizona",
      signals: ["golf", "clubs", "healthcare", "large_scale"],
      note: "Original 55+ model with rec centers, clubs, golf, and on-site hospital signal.",
    },
    {
      source: "Explore55Plus",
      name: "Sun City Texas",
      location: "Austin Area, Texas",
      state: "Texas",
      price: "$300K-$700K",
      hoa: "$160-$400/mo",
      url: "https://www.explore55plus.com/community/sun-city-texas-texas",
      signals: ["golf", "pickleball", "clubs", "new_build", "large_scale"],
      note: "Large Texas retirement node with golf, pickleball, and club density.",
    },
  ],
};

export const painSummary: PainSummary = {
  generatedAt: "2026-05-16",
  corpus: {
    threads: 548,
    sampledComments: 479,
    subreddits: [
      "retirement",
      "financialindependence",
      "ExpatFIRE",
      "fatFIRE",
      "ChubbyFIRE",
      "personalfinance",
      "SameGrassButGreener",
      "florida",
    ],
    sourceCount: 117,
    failedSourceCount: 26,
  },
  categories: [
    {
      id: "money_adequacy",
      label: "Money adequacy",
      threadCount: 408,
      commentCount: 135,
      plainLanguage: "Do I have enough, and what if I am wrong?",
      likelyPaidHelp: [
        "fee-only financial plan",
        "retirement readiness memo",
        "withdrawal and Social Security timing review",
      ],
    },
    {
      id: "spouse_family",
      label: "Spouse and family",
      threadCount: 323,
      commentCount: 61,
      plainLanguage: "Retirement changes family obligations, couple alignment, and adult-child pressure.",
      likelyPaidHelp: ["family retirement memo", "couple alignment checklist", "parent retirement reality check"],
    },
    {
      id: "place_relocation",
      label: "Place and relocation",
      threadCount: 312,
      commentCount: 47,
      plainLanguage: "Where should I live, and will I regret moving?",
      likelyPaidHelp: ["place-fit dossier", "trial-stay plan", "healthcare/social radius map"],
    },
    {
      id: "housing_home",
      label: "Housing and home",
      threadCount: 306,
      commentCount: 65,
      plainLanguage: "The home is an asset, cost center, lifestyle platform, and future care risk.",
      likelyPaidHelp: ["downsize/stay/move memo", "home equity decision review", "decline-readiness scan"],
    },
    {
      id: "healthcare_medicare",
      label: "Healthcare and Medicare",
      threadCount: 229,
      commentCount: 54,
      plainLanguage: "Healthcare blocks or destabilizes the retirement decision.",
      likelyPaidHelp: ["Medicare decision support", "pre-65 healthcare bridge review", "expat healthcare comparison"],
    },
    {
      id: "regret_uncertainty",
      label: "Regret and uncertainty",
      threadCount: 216,
      commentCount: 38,
      plainLanguage: "The decision feels irreversible and emotionally loaded.",
      likelyPaidHelp: ["second-opinion memo", "pre-mortem", "scenario plan"],
    },
    {
      id: "loneliness_social",
      label: "Loneliness and social life",
      threadCount: 204,
      commentCount: 38,
      plainLanguage: "How do I avoid isolation after leaving work or moving?",
      likelyPaidHelp: ["social infrastructure map", "retirement community-fit plan", "recurring activity/rhythm builder"],
    },
    {
      id: "identity_purpose",
      label: "Identity and purpose",
      threadCount: 134,
      commentCount: 19,
      plainLanguage: "What replaces work, status, structure, and mattering?",
      likelyPaidHelp: ["first-90-days retirement plan", "purpose and routine design", "community/volunteering map"],
    },
  ],
  paySignals: [
    { id: "legal_estate", label: "Legal / estate", postCount: 268, commentCount: 83 },
    { id: "coaching_course", label: "Coaching / course", postCount: 62, commentCount: 13 },
    { id: "financial_planner", label: "Financial planner", postCount: 21, commentCount: 10 },
    { id: "tax_cpa", label: "Tax / CPA", postCount: 18, commentCount: 3 },
    { id: "care_management", label: "Care management", postCount: 7, commentCount: 3 },
    { id: "insurance", label: "Insurance", postCount: 5, commentCount: 1 },
    { id: "relocation_help", label: "Relocation help", postCount: 4, commentCount: 0 },
  ],
  bestInitialOfferHypothesis:
    "Retirement Life-System Dossier, starting with place-fit and retirement-readiness decisions.",
};

export const locationRows: LocationRow[] = [
  {
    id: "florida_overall",
    location: "Florida",
    region: "United States / Southeast",
    archetype: "classic warm-weather U.S. retirement state",
    bestFit: ["tax-sensitive U.S. retirees", "sun seekers", "Medicare-age retirees"],
    avoidIf: ["insurance volatility is unacceptable", "heat/humidity is medically hard", "car dependence is a dealbreaker"],
    reviewSignal:
      "Dominant retirement magnet with growing conflict around insurance, heat, storms, cost, politics, and healthcare variation.",
    riskFlags: ["homeowners insurance", "flood insurance", "hurricanes", "heat", "car dependence"],
    evidenceLinks: [
      "https://www.kiplinger.com/retirement/why-do-people-retire-in-florida-what-you-must-know",
      "https://www.prnewswire.com/news-releases/us-news--world-report-announces-the-2026-best-places-to-retire-302602837.html",
    ],
    evidenceConfidence: "medium",
  },
  {
    id: "the_villages",
    location: "The Villages / Central Florida",
    region: "United States / Florida",
    archetype: "purpose-built active adult ecosystem",
    bestFit: ["social joiners", "golf/cart lifestyle retirees", "55+ community seekers"],
    avoidIf: ["age-segregated culture feels depressing", "multi-age city life matters", "HOA politics are intolerable"],
    reviewSignal: "Strong for built-in activities and peer density; weaker for retirees who dislike old-age monoculture.",
    riskFlags: ["age monoculture", "heat", "community politics", "car/golf-cart lifestyle"],
    evidenceLinks: [
      "https://assets.newsweek.com/wp-content/uploads/2025/11/20251015_Methodology_Americas-Best-Continuing-Care-Retirement-Communities_2026.pdf",
    ],
    evidenceConfidence: "low",
  },
  {
    id: "hua_hin",
    location: "Hua Hin",
    region: "Thailand / Prachuap Khiri Khan",
    archetype: "calm Thai coastal retirement capital",
    bestFit: ["expat retirees", "golfers", "beach/cafe routine people", "health-constrained lifestyle seekers"],
    avoidIf: ["under-50 social life is essential", "walkability without scooter/car is required", "visa uncertainty is unacceptable"],
    reviewSignal: "Stable and retiree-heavy; calmer than Pattaya, but younger people may struggle socially.",
    riskFlags: ["visa rules", "health insurance", "transport/walkability", "language", "serious-care transfer to Bangkok"],
    evidenceLinks: [
      "https://www.propertysolutionshuahin.com/th/best-areas-hua-hin-retirees/",
      "https://www.reddit.com/r/HuaHin/comments/1r8u3aq/moving_to_hua_hin_any_advice/",
    ],
    evidenceConfidence: "medium",
  },
  {
    id: "chiang_mai",
    location: "Chiang Mai",
    region: "Thailand / Northern Thailand",
    archetype: "low-cost culture/food expat hub",
    bestFit: ["budget expats", "digital nomad adjacent retirees", "culture/food people"],
    avoidIf: ["smoke season is medically risky", "coastal life is required", "Bangkok-level specialty care proximity is required"],
    reviewSignal: "Cheaper and more mixed-age than Hua Hin, but smoke season is a serious quality-of-life constraint.",
    riskFlags: ["smoke season", "heat", "visa/admin", "distance from Bangkok and coast"],
    evidenceLinks: ["https://www.reddit.com/r/ExpatFIRE/comments/1mr8754/went_down_the_rabbit_hole_and_calculated_my/"],
    evidenceConfidence: "low",
  },
  {
    id: "bangkok",
    location: "Bangkok",
    region: "Thailand / Bangkok Metro",
    archetype: "healthcare-first Asian city base",
    bestFit: ["healthcare-first expats", "urban retirees", "Asia-connected people"],
    avoidIf: ["quiet small-city retirement is required", "traffic/heat overwhelms", "beach/calm is the point"],
    reviewSignal: "ExpatFIRE comments treat Bangkok healthcare as a major U.S. alternative while noting private hospital cost.",
    riskFlags: ["traffic", "heat", "urban intensity", "private hospital cost", "insurance requirements"],
    evidenceLinks: [
      "https://www.reddit.com/r/ExpatFIRE/comments/1ndn3al/anyone_else_feel_like_they_could_have_retired_in/",
      "https://www.reddit.com/r/ExpatFIRE/comments/1qn53s1/whats_the_number_to_hit_to_retire_in_bangkok/",
    ],
    evidenceConfidence: "medium",
  },
  {
    id: "valencia_malaga_spain",
    location: "Valencia / Malaga / Spain",
    region: "Spain",
    archetype: "European city/coastal retirement with healthcare value",
    bestFit: ["culture retirees", "healthcare-value seekers", "car-light lifestyle seekers"],
    avoidIf: ["tax complexity is unacceptable", "language/admin tolerance is low", "U.S. proximity to family is crucial"],
    reviewSignal: "Healthcare comparisons make Spain compelling, but taxes and residency math must be included.",
    riskFlags: ["tax residency", "bureaucracy", "language", "housing pressure", "healthcare wait differences"],
    evidenceLinks: [
      "https://www.reddit.com/r/ExpatFIRE/comments/1sftfz4/i_ran_the_10year_healthcare_numbers_for_retiring/",
      "https://www.reddit.com/r/ExpatFIRE/comments/1mblms8/taxes_are_killing_my_plan_in_spain_where_to_look",
    ],
    evidenceConfidence: "medium",
  },
  {
    id: "portugal_algarve_lagos",
    location: "Algarve / Lagos / Portugal",
    region: "Portugal",
    archetype: "global-retirement media favorite, coastal Europe",
    bestFit: ["coastal Europe seekers", "U.S./UK expats", "slower-life retirees"],
    avoidIf: ["they expect old Portugal prices", "tax/visa changes are not tolerable", "housing pressure is a dealbreaker"],
    reviewSignal: "Highly visible in expat retirement lists and ExpatFIRE discussions; needs current tax/visa/housing checks.",
    riskFlags: ["rising housing", "tax changes", "visa bureaucracy", "language", "healthcare access by region"],
    evidenceLinks: [
      "https://www.reddit.com/r/ExpatFIRE/comments/1t5yqmd/laid_off_at_34_am_i_ready_to_retire_to_portugal/",
    ],
    evidenceConfidence: "low",
  },
  {
    id: "manhattan_nyc",
    location: "Manhattan / New York City",
    region: "United States / New York",
    archetype: "dense culture retirement",
    bestFit: ["urban retirees", "arts/culture people", "transit-dependent retirees", "people with controlled housing costs"],
    avoidIf: ["housing is market-rate and budget is modest", "noise/stairs/safety perception are intolerable", "low-tax climate is the goal"],
    reviewSignal: "Can be rational when housing is controlled and culture/transit matter more than cheapness.",
    riskFlags: ["housing cost", "taxes", "noise", "winter", "stairs/accessibility", "safety perception"],
    evidenceLinks: ["https://www.reddit.com/r/retirement/comments/1n8u5wp/retirement_to_manhattan_am_i_crazy/"],
    evidenceConfidence: "medium",
  },
  {
    id: "las_cruces",
    location: "Las Cruces",
    region: "United States / New Mexico",
    archetype: "emerging cheaper Southwest alternative",
    bestFit: ["snowbirds", "desert/outdoor retirees", "Florida/Phoenix avoiders"],
    avoidIf: ["big-city healthcare depth is required", "small-city life feels limiting", "flight access matters"],
    reviewSignal: "Flagged as cheaper and less crowded than Florida, Phoenix, and Vegas.",
    riskFlags: ["smaller-city healthcare", "heat", "limited flights", "less retiree infrastructure"],
    evidenceLinks: ["https://www.reddit.com/r/SameGrassButGreener/comments/1ssnnqr/what_are_some_obscure_cities_that_will_become/"],
    evidenceConfidence: "low",
  },
  {
    id: "penang",
    location: "Penang",
    region: "Malaysia",
    archetype: "English-friendly medical/value retirement",
    bestFit: ["Asia expats", "food/culture retirees", "healthcare-value seekers"],
    avoidIf: ["visa instability is intolerable", "humidity is medically hard", "U.S. proximity is important"],
    reviewSignal: "Common global retirement-list candidate because of healthcare, English, food, and value mix.",
    riskFlags: ["visa program changes", "humidity", "distance from family", "insurance"],
    evidenceLinks: ["https://internationalliving.com/countries/guatemala/"],
    evidenceConfidence: "low",
  },
  {
    id: "mexico_merida_lake_chapala",
    location: "Merida / Lake Chapala / San Miguel",
    region: "Mexico",
    archetype: "near-U.S. culture and cost retirement",
    bestFit: ["U.S./Canada expats", "warm-climate seekers", "culture/cost seekers"],
    avoidIf: ["Spanish/admin adaptation is intolerable", "safety anxiety dominates", "U.S. Medicare access is needed continuously"],
    reviewSignal: "Mexico appears in retirement, snowbird, and cost discussions; proximity is the strategic advantage.",
    riskFlags: ["healthcare variance", "heat", "language", "visa/residency", "safety perception"],
    evidenceLinks: ["https://www.reddit.com/r/retirement/comments/1sj2t1t/today_is_my_first_day_of_retirement/"],
    evidenceConfidence: "low",
  },
  {
    id: "da_nang",
    location: "Da Nang",
    region: "Vietnam",
    archetype: "lower-cost coastal Asia experiment",
    bestFit: ["adventurous ExpatFIRE", "younger retirees", "low-cost Asia seekers"],
    avoidIf: ["stable long-term retirement visa is required", "high-acuity healthcare is likely", "language/admin friction is unacceptable"],
    reviewSignal: "More like a sabbatical or early-retirement base than a classic aging base.",
    riskFlags: ["visa stability", "healthcare depth", "language", "long-term legal status"],
    evidenceLinks: [
      "https://www.reddit.com/r/ExpatFIRE/comments/1osdkql/is_600k_enough_to_retire_in_da_nang_asuncion/",
      "https://www.reddit.com/r/ExpatFIRE/comments/1t1ep0m/month_5_of_world_traveling_da_nang/",
    ],
    evidenceConfidence: "low",
  },
];

export const resourceRows: ResourceRow[] = [
  {
    id: "active-adult-catalog",
    title: "Active adult listing catalog",
    kind: "data",
    path: "data/active_adult_listing_catalog.json",
    focus: "Summarized 55+ community inventory from Explore55Plus and Where55.",
    rowCount: 678,
  },
  {
    id: "reddit-summary",
    title: "Reddit retirement pain summary",
    kind: "data",
    path: "data/reddit_summary.json",
    focus: "Counts pain categories and paid-help signals across retirement-related Reddit threads and comments.",
    rowCount: 548,
  },
  {
    id: "pain-points",
    title: "Pain point atlas",
    kind: "model",
    path: "data/pain_points.json",
    focus: "Plain-language retirement anxieties with likely paid-help shapes.",
    rowCount: 8,
  },
  {
    id: "retirement-locations",
    title: "Retirement location rows",
    kind: "data",
    path: "data/retirement_locations.json",
    focus: "Normalized retirement-place archetypes, fit, avoidance criteria, risks, and evidence links.",
    rowCount: 12,
  },
  {
    id: "embodied-insights",
    title: "Active Adult Embodied Insights",
    kind: "analysis",
    path: "ACTIVE_ADULT_EMBODIED_INSIGHTS.md",
    focus: "Interpretive read of what active-adult listing sites make legible and what they underweight.",
  },
  {
    id: "source-map",
    title: "Retirement thesis source map",
    kind: "source-map",
    path: "SOURCE_MAP.md",
    focus: "Human-readable index for the thesis artifacts and evidence trails.",
  },
  {
    id: "location-atlas",
    title: "Location Atlas",
    kind: "analysis",
    path: "LOCATION_ATLAS.md",
    focus: "Narrative location analysis behind the normalized location rows.",
  },
  {
    id: "site-adapter",
    title: "Site data adapter",
    kind: "site",
    path: "site/src/data/atlasData.ts",
    focus: "Compact UI-ready constants for prototype dashboard screens.",
  },
];

export const dashboardMetrics: DashboardMetric[] = [
  {
    label: "Rich active-adult rows",
    value: "678",
    detail: "670 Explore55Plus cards plus 8 Where55 rich rows; Where55 also has 2,412 sitemap URLs.",
  },
  {
    label: "Median community price band",
    value: "$300K-$800K",
    detail: "Median lower and upper bounds across priced Explore55Plus rich rows.",
  },
  {
    label: "Reddit threads analyzed",
    value: "548",
    detail: "Retirement, FIRE, relocation, and Florida discussions with 479 sampled comments.",
  },
  {
    label: "Top pain signal",
    value: "Money adequacy",
    detail: "408 threads and 135 sampled comments ask whether the retirement plan is enough.",
  },
  {
    label: "Location archetypes",
    value: "12",
    detail: "Rows span U.S. Sunbelt, dense urban, Europe, Mexico, and Southeast Asia retirement patterns.",
  },
];
